const DB_NAME = 'AISubHelperDB';
const STORE_NAME = 'subscriptions';
const DB_VERSION = 1; // Increment this if schema changes

let db = null;

/**
 * Opens the IndexedDB database connection.
 * Creates the object store and indexes if they don't exist.
 * @returns {Promise<IDBDatabase>} A promise that resolves with the database instance.
 */
function openDB() {
  return new Promise((resolve, reject) => {
    // If db instance already exists, return it
    if (db) {
      resolve(db);
      return;
    }

    console.log('[DB] Opening IndexedDB...');
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('[DB] Error opening database:', event.target.error);
      reject(`Database error: ${event.target.error}`);
    };

    request.onsuccess = (event) => {
      db = event.target.result;
      console.log('[DB] Database opened successfully.');
      db.onerror = (event) => {
          // Generic error handler for all errors targeted at this database's connection
          console.error(`[DB] Generic database error: ${event.target.error}`);
      };
      resolve(db);
    };

    // This event is only fired when the version changes
    // or the database is created for the first time.
    request.onupgradeneeded = (event) => {
      console.log('[DB] Upgrade needed or database creation...');
      const tempDb = event.target.result;
      const transaction = event.target.transaction; // Get transaction from event

      if (!tempDb.objectStoreNames.contains(STORE_NAME)) {
        console.log(`[DB] Creating object store: ${STORE_NAME}`);
        const store = tempDb.createObjectStore(STORE_NAME, { keyPath: 'id' });
        // Create an index to search/sort by last updated date
        store.createIndex('updatedAt', 'updatedAt', { unique: false });
         console.log('[DB] Index "updatedAt" created.');
        // Optional: Create an index for easily finding local items
        // store.createIndex('isLocal', 'isLocal', { unique: false });
      } else {
        // Handle potential future schema migrations here if DB_VERSION increases
        console.log(`[DB] Object store "${STORE_NAME}" already exists.`);
        // Example: Check if an index needs to be added in a later version
        // const store = transaction.objectStore(STORE_NAME);
        // if (!store.indexNames.contains('newIndex')) {
        //     store.createIndex('newIndex', 'newIndexField', { unique: false });
        //     console.log('[DB] Index "newIndex" created.');
        // }
      }
       console.log('[DB] Database upgrade complete.');
    };
  });
}

/**
 * Performs a database transaction.
 * @param {string} storeName - The name of the object store.
 * @param {IDBTransactionMode} mode - Transaction mode ('readonly' or 'readwrite').
 * @param {(store: IDBObjectStore) => Promise<any>} action - Callback function performing actions on the store.
 * @returns {Promise<any>} Result of the action.
 */
async function performTransaction(storeName, mode, action) {
    const dbInstance = await openDB(); // Ensure DB is open
    return new Promise((resolve, reject) => {
        const transaction = dbInstance.transaction(storeName, mode);
        const store = transaction.objectStore(storeName);
        let requestResult = null; // To store result from action if it's a request

        transaction.oncomplete = () => {
            console.log(`[DB Transaction ${mode}] Completed for ${storeName}.`);
            resolve(requestResult); // Resolve with the request result if any
        };

        transaction.onerror = (event) => {
            console.error(`[DB Transaction ${mode}] Error for ${storeName}:`, event.target.error);
            reject(`Transaction error: ${event.target.error}`);
        };

        // Execute the action callback, passing the store
        try {
            const actionResult = action(store);
            // If action returns an IDBRequest, capture its result/error
            if (actionResult instanceof IDBRequest) {
                 actionResult.onsuccess = (event) => {
                     requestResult = event.target.result; // Capture result for oncomplete
                 };
                 actionResult.onerror = (event) => {
                     // Error is already handled by transaction.onerror, but log specific request error
                     console.error('[DB Request] Error during action:', event.target.error);
                     // No need to reject here, transaction.onerror will handle it
                 };
            } else if (actionResult instanceof Promise) {
                // If the action is async itself, wait for it
                 actionResult.then(resolve).catch(reject); // Resolve/reject based on the promise outcome
                 // Note: This bypasses transaction.oncomplete resolution for Promises. Adjust if needed.
            } else {
                 requestResult = actionResult; // Assume synchronous result, will be resolved by oncomplete
            }
        } catch (error) {
             console.error(`[DB Action] Error executing action for ${storeName}:`, error);
             reject(`Action execution error: ${error}`);
             // Attempt to abort the transaction on synchronous errors within the action
             try {
                 transaction.abort();
             } catch (abortError) {
                 console.error('[DB Action] Error aborting transaction:', abortError);
             }
        }
    });
}


// --- Public API ---

/**
 * Initializes the database connection. Call this on application startup.
 * @returns {Promise<void>}
 */
export async function initDB() {
  try {
    await openDB();
    console.log("[Storage] Database initialized successfully.");
  } catch (error) {
    console.error("[Storage] Failed to initialize database:", error);
    // Consider notifying the user or attempting recovery
  }
}

/**
 * Adds or updates a single subscription in the database.
 * Generates a temporary ID if none is provided.
 * Sets createdAt and updatedAt timestamps.
 * @param {object} subscription - The subscription object.
 * @returns {Promise<string>} The ID of the added/updated subscription.
 */
export async function addOrUpdateSubscription(subscription) {
    return performTransaction(STORE_NAME, 'readwrite', (store) => {
        const now = new Date().toISOString();
        const record = {
            ...subscription,
            // Assign temp ID if no ID exists
            id: subscription.id || `temp-${crypto.randomUUID()}`,
            // Set createdAt only if it's a new record (or doesn't exist)
            createdAt: subscription.createdAt || now,
            updatedAt: now, // Always update updatedAt
        };
        console.log('[Storage] Adding/Updating subscription:', record.id);
        return store.put(record); // put handles both add and update
    });
}


/**
 * Gets a single subscription by its ID.
 * @param {string} id - The ID of the subscription.
 * @returns {Promise<object | undefined>} The subscription object or undefined if not found.
 */
export async function getSubscription(id) {
  console.log('[Storage] Getting subscription:', id);
  return performTransaction(STORE_NAME, 'readonly', (store) => {
    return store.get(id);
  });
}

/**
 * Gets all subscriptions from the database, sorted by updatedAt descending.
 * @returns {Promise<Array<object>>} An array of subscription objects.
 */
export async function getAllSubscriptions() {
  console.log('[Storage] Getting all subscriptions...');
  return performTransaction(STORE_NAME, 'readonly', async (store) => {
      // Use the index to sort by updatedAt. getAll() gets all records.
      // We sort afterwards as direct indexed getAll + sort isn't standard.
      const allSubs = await new Promise((resolve, reject) => {
            const request = store.getAll();
            request.onsuccess = (event) => resolve(event.target.result);
            request.onerror = (event) => reject(event.target.error);
      });

      // Sort in descending order of updatedAt
      allSubs.sort((a, b) => {
           try {
               // Ensure valid dates, fallback to 0 if invalid
               const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
               const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
               return dateB - dateA;
           } catch (e) {
                console.warn("[Storage] Error comparing dates during sort:", e);
                return 0;
           }
      });
      console.log(`[Storage] Retrieved ${allSubs.length} subscriptions.`);
      return allSubs;
  });
}

/**
 * Deletes a single subscription by its ID.
 * @param {string} id - The ID of the subscription to delete.
 * @returns {Promise<void>}
 */
export async function deleteSubscription(id) {
  console.log('[Storage] Deleting subscription:', id);
  return performTransaction(STORE_NAME, 'readwrite', (store) => {
    return store.delete(id);
  });
}

/**
 * Retrieves all subscriptions that have a temporary ID (start with "temp-").
 * @returns {Promise<Array<object>>} An array of unsynced subscription objects.
 */
export async function getLocalUnsyncedSubscriptions() {
    console.log('[Storage] Getting local unsynced subscriptions...');
    const allSubs = await getAllSubscriptions(); // Reuse getAll and filter
    const unsynced = allSubs.filter(sub => sub && sub.id && typeof sub.id === 'string' && sub.id.startsWith('temp-'));
    console.log(`[Storage] Found ${unsynced.length} unsynced subscriptions.`);
    return unsynced;
}

/**
 * Adds or updates multiple subscriptions in a single transaction.
 * Useful for syncing data from the server.
 * @param {Array<object>} subscriptions - An array of subscription objects.
 * @returns {Promise<void>}
 */
export async function bulkAddOrUpdateSubscriptions(subscriptions) {
    if (!subscriptions || subscriptions.length === 0) {
        console.log('[Storage] bulkAddOrUpdate: No subscriptions provided.');
        return Promise.resolve();
    }
    console.log(`[Storage] Bulk adding/updating ${subscriptions.length} subscriptions...`);
    return performTransaction(STORE_NAME, 'readwrite', (store) => {
        const promises = subscriptions.map(sub => {
             // Basic validation
             if (!sub || !sub.id) {
                 console.warn("[Storage] Skipping invalid record in bulk update:", sub);
                 return Promise.resolve(); // Skip invalid records
             }
             const now = new Date().toISOString();
             const record = {
                 ...sub,
                 // Ensure timestamps exist, prefer existing ones unless updating
                 createdAt: sub.createdAt || now,
                 updatedAt: now, // Assume bulk update means they are now "updated" locally
             };
             // Return the promise from the put request
             return new Promise((resolvePut, rejectPut) => {
                 const req = store.put(record);
                 req.onsuccess = resolvePut;
                 req.onerror = () => rejectPut(req.error);
             });
        });
        // Wait for all put operations in the transaction to be requested
        // Transaction completion is handled by performTransaction wrapper
        return Promise.all(promises);
    });
}

/**
 * Deletes multiple subscriptions by their IDs in a single transaction.
 * @param {Array<string>} ids - An array of subscription IDs to delete.
 * @returns {Promise<void>}
 */
export async function bulkDeleteSubscriptions(ids) {
    if (!ids || ids.length === 0) {
        console.log('[Storage] bulkDelete: No IDs provided.');
        return Promise.resolve();
    }
     console.log(`[Storage] Bulk deleting ${ids.length} subscriptions...`);
     return performTransaction(STORE_NAME, 'readwrite', (store) => {
        const promises = ids.map(id => {
            if (!id) {
                console.warn("[Storage] Skipping invalid ID in bulk delete:", id);
                return Promise.resolve();
            }
            return new Promise((resolveDelete, rejectDelete) => {
                const req = store.delete(id);
                req.onsuccess = resolveDelete;
                req.onerror = () => rejectDelete(req.error);
            });
        });
        return Promise.all(promises);
     });
}


/**
 * Clears all data from the subscriptions object store. Use with caution!
 * @returns {Promise<void>}
 */
export async function clearAllSubscriptions() {
    console.warn('[Storage] Clearing ALL subscriptions from IndexedDB!');
    return performTransaction(STORE_NAME, 'readwrite', (store) => {
        return store.clear();
    });
}

// Optional: Initialize DB automatically when module loads
// initDB(); // Or call explicitly from main.js
